import { type JSX } from "react";
import type { LLMRequest } from "./types";
import {
  formatJSON,
  parsePromptContent,
  copyToClipboard,
  type CalculatedStats,
} from "./llmRequestFormatting";

export type TabId = "prompt" | "response" | "analysis";

const TABS: { id: TabId; name: string }[] = [
  { id: "prompt", name: "Prompt" },
  { id: "response", name: "Response" },
  { id: "analysis", name: "Analysis" },
];

export function TabNav({
  activeTab,
  onSelect,
}: {
  activeTab: TabId;
  onSelect: (tab: TabId) => void;
}): JSX.Element {
  return (
    <div className="border-b border-gray-200">
      <nav className="-mb-px flex space-x-8">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => onSelect(tab.id)}
            className={`py-4 px-6 border-b-2 font-medium text-sm ${
              activeTab === tab.id
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            {tab.name}
          </button>
        ))}
      </nav>
    </div>
  );
}

export function PromptTab({
  llmRequest,
  stats,
}: {
  llmRequest: LLMRequest;
  stats: CalculatedStats;
}): JSX.Element {
  const promptText = parsePromptContent(llmRequest.prompt);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-lg font-medium text-gray-900">Full Prompt</h4>
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-500">{stats.promptTokens} tokens</div>
          <button
            onClick={() => void copyToClipboard(promptText)}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            title="Copy prompt to clipboard"
          >
            Copy
          </button>
        </div>
      </div>
      <div className="bg-gray-50 p-4 rounded-md max-h-96 overflow-y-auto">
        <pre className="text-sm text-gray-900 whitespace-pre-wrap font-sans">{promptText}</pre>
      </div>
    </div>
  );
}

export function ResponseTab({
  llmRequest,
  stats,
}: {
  llmRequest: LLMRequest;
  stats: CalculatedStats;
}): JSX.Element {
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-lg font-medium text-gray-900">Full Response</h4>
        <div className="text-sm text-gray-500">{stats.responseTokens} tokens</div>
      </div>
      <div className="bg-blue-50 p-4 rounded-md max-h-96 overflow-y-auto">
        <pre className="text-sm text-gray-900 whitespace-pre-wrap">
          {formatJSON(llmRequest.response)}
        </pre>
      </div>
    </div>
  );
}

function CharacteristicsGrid({
  llmRequest,
  stats,
}: {
  llmRequest: LLMRequest;
  stats: CalculatedStats;
}): JSX.Element {
  const promptText = parsePromptContent(llmRequest.prompt);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <h5 className="font-medium text-gray-700 mb-2">Prompt Characteristics</h5>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• Length: {promptText.length} characters</li>
          <li>• Tokens: {stats.promptTokens}</li>
          <li>• Lines: {promptText.split("\n").length}</li>
          <li>• Model: {llmRequest.model}</li>
        </ul>
      </div>

      <div>
        <h5 className="font-medium text-gray-700 mb-2">Response Characteristics</h5>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• Length: {llmRequest.response.length} characters</li>
          <li>• Tokens: {stats.responseTokens}</li>
          <li>• Processing Time: {stats.processingTime}ms</li>
          <li>• Cost: {stats.totalTokens > 0 ? `$${stats.cost.toFixed(6)}` : "N/A"}</li>
        </ul>
      </div>
    </div>
  );
}

function EfficiencyMetric({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <span className="font-medium">{label}</span>
      <div className="text-gray-600">{value}</div>
    </div>
  );
}

function EfficiencyMetrics({ stats }: { stats: CalculatedStats }): JSX.Element {
  const hasTokens = stats.totalTokens > 0;

  return (
    <div>
      <h5 className="font-medium text-gray-700 mb-2">Efficiency Metrics</h5>
      <div className="bg-gray-50 p-4 rounded-md">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <EfficiencyMetric
            label="Token Efficiency:"
            value={
              hasTokens
                ? `${((stats.responseTokens / stats.totalTokens) * 100).toFixed(1)}% response`
                : "N/A"
            }
          />
          <EfficiencyMetric
            label="Speed:"
            value={stats.tokensPerSecond > 0 ? `${stats.tokensPerSecond} tokens/sec` : "N/A"}
          />
          <EfficiencyMetric
            label="Cost per Token:"
            value={hasTokens ? `$${(stats.cost / stats.totalTokens).toFixed(8)}` : "N/A"}
          />
        </div>
      </div>
    </div>
  );
}

export function AnalysisTab({
  llmRequest,
  stats,
}: {
  llmRequest: LLMRequest;
  stats: CalculatedStats;
}): JSX.Element {
  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-medium text-gray-900 mb-3">Request Analysis</h4>
        <CharacteristicsGrid llmRequest={llmRequest} stats={stats} />
      </div>
      <EfficiencyMetrics stats={stats} />
    </div>
  );
}
